import React, { useRef } from "react"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPORT_ENTITIES_ROUTE, SUPPORT_ROUTE } from "../../../../../../constants/routes"
import { FormikProps } from "formik"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { enqueueSnackbar } from "notistack"
import { createSupplierApi } from "../createSupplierApi"
import { Button } from "../../../../../office/admin/components/Button"
import { useNavigate } from "react-router"
import { SupplierFormValues } from "../supplierTypes"
import { SupplierCreateUpdateForm } from "../components/SupplierCreateUpdateForm"
import { AgreementOptions, BusinessType } from "../../supportEntitiesTypes"
import { getAddress, getContactDetails, getInvoiceDetails } from "../../helpers"
import { SupportEntityQuery } from "../../supportEntityApi"

export function CreateSupplier() {
    const navigate = useNavigate()
    const supplierFormRef = useRef<FormikProps<SupplierFormValues>>(null)

    const { mutate } = useApiMutation(createSupplierApi, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupportEntityQuery.LIST] })
            navigate(SUPPORT_ENTITIES_ROUTE)
            if (data?.serviceResponse?.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse?.displayMessage,
                })
            }
        },
    })

    const handleSaveClick = () => {
        supplierFormRef.current?.submitForm()
    }

    const createSupplier = (value: SupplierFormValues) => {
        mutate({
            entity: {
                isAgreementOwner: value.isSystemOwner === AgreementOptions.YES,
                businessType: value.businessType as BusinessType,
                businessNumber: value.businessNumber,
                name: value.name,
                address: getAddress(value),
                contactDetails: getContactDetails(value),
                ...getInvoiceDetails(value),
                isActive: value.isActive,
                ...(value.implementationDeadline
                    ? {
                          subscriptionDetails: {
                              implementationDeadline: value.implementationDeadline.toISODate(),
                          },
                      }
                    : {}),
            },
        })
    }

    return (
        <>
            <LocalizedStrict id="support-create-supplier-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_CREATE_SUPPLIER}
                    title="Create supplier"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="support-create-supplier-create-button">
                            <Button variant="contained" onClick={handleSaveClick}>
                                Create
                            </Button>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-create-supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-create-supplier-nav-item-entities">
                            <BreadcrumbLink to={SUPPORT_ENTITIES_ROUTE}>Entities</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-create-supplier-nav-item-create-supplier">
                            <Breadcrumb>Create supplier</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <SupplierCreateUpdateForm ref={supplierFormRef} onSubmit={createSupplier} />
        </>
    )
}
